import React, { useState, FC } from 'react';

import { Box, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { DataSecurityFilters } from 'containers/Visibility/Data/Components/DataTable/DataSecurityFilters';
import { DataSourcesByResourceType } from 'containers/Visibility/Data/Components/DataTable/DataSourcesByResourceType';
import { DocumentSubTypes } from 'containers/Visibility/Data/Components/DataTable/DocumentSubTypes';
import { DocumentTypeDataSources } from 'containers/Visibility/Data/Components/DataTable/DocumentTypeDataSources';
import { SourcesByDataType } from 'containers/Visibility/Data/Components/DataTable/SourcesByDataType';
import { SyncLabels } from 'containers/Visibility/Data/Components/DataTable/SyncLabels';
import { useDataTableColumns } from 'containers/Visibility/Data/Components/hooks/useDataTableColumns';
// Disabled until the backend sensitive-records API is available.
// import { useSensitiveRecordsByResource } from 'containers/Visibility/Data/Components/hooks/useSensitiveRecordsByResource';
import { renderDataSecurityHeader } from 'containers/Visibility/Data/Components/utils';
import { selectDataDashboard } from 'containers/Visibility/Data/selectors';
import { actions } from 'containers/Visibility/Data/slice';

export const DataTableComponent: FC = () => {
  const {
    selectedTab,
    dataSources,
    dataSecurityIdentities,
    allDataResourceTypes,
  } = useSelector(selectDataDashboard);
  const [isExpanded, setIsExpanded] = useState(false);
  const isIdentityView = selectedTab === 'Identities';
  // The current labels-count API does not return the right numbers for
  // identities, so the call is disabled and Sensitive Records shows '-'.
  // Re-enable (or swap in the new endpoint) once the backend provides it.
  // const sensitiveRecordsByResource = useSensitiveRecordsByResource(
  //   dataSecurityIdentities.data,
  //   isIdentityView,
  // );
  const sensitiveRecordsByResource: Record<string, number> = {};
  const {
    tableColumns,
    tableActionModals,
    handleRowClick,
    openIdentity,
  } = useDataTableColumns(MetadataTabs.Data, sensitiveRecordsByResource);

  const dispatch = useDispatch();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const subTypeDisclosure = useDisclosure();
  const dataSourcesDisclosure = useDisclosure();
  const dataTypeSourcesDisclosure = useDisclosure();
  const dataSourcesByResourceType = useDisclosure();
  const syncLabelsDisclosure = useDisclosure();

  const renderTable = () => (
    <Stack h="full" spacing={3} w="full">
      <HStack>
        <DataSecurityFilters
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          onOpenSyncLabels={syncLabelsDisclosure.onOpen}
        />
      </HStack>

      <Box flex={1} pt={3} pos="relative">
        <Table
          noDataMessage={
            selectedTab === 'Identities'
              ? 'No identities with sensitive data access'
              : selectedTab === 'DataTypes'
              ? 'No data types detected'
              : 'No data sources being analyzed'
          }
          data={
            selectedTab === 'Identities'
              ? dataSecurityIdentities.data
              : selectedTab === 'all'
              ? allDataResourceTypes.data
              : dataSources.data
          }
          columns={tableColumns}
          isLoading={
            isIdentityView
              ? dataSecurityIdentities.isLoading
              : selectedTab === 'all'
              ? allDataResourceTypes.isLoading
              : dataSources.isLoading
          }
          cursor={
            isIdentityView ||
            selectedTab === IconTypes.DataSources ||
            selectedTab === 'DataTypes'
              ? 'pointer'
              : 'default'
          }
          styles={{ header: { position: 'relative', zIndex: 800 } }}
          {...(selectedTab === 'all'
            ? {}
            : {
                pagination: {
                  totalCount: isIdentityView
                    ? dataSecurityIdentities.page.totalCount
                    : dataSources?.page.totalCount,
                  pageInfo: isIdentityView
                    ? dataSecurityIdentities.page.info
                    : dataSources.page.info,
                  onChange: pageInfo => {
                    dispatch(
                      isIdentityView
                        ? actions.updateDataSecurityIdentitiesPageInfo(pageInfo)
                        : actions.updateDataTablePageInfo(pageInfo),
                    );
                  },
                },
              })}
          onRowClick={row => {
            setCurrentRecord(row);
            if (isIdentityView) {
              openIdentity(row);
            } else if (selectedTab === 'DataSources') {
              handleRowClick(row, false);
            } else if (selectedTab === 'DocumentTypes') {
              subTypeDisclosure.onOpen();
            } else if (selectedTab === 'DataTypes') {
              // Keep the Data Types view selected while showing its sources in the drawer.
              dataTypeSourcesDisclosure.onOpen();
            } else {
              dataSourcesByResourceType.onOpen();
            }
          }}
        />
        {!isIdentityView && tableActionModals}
      </Box>
      {subTypeDisclosure.isOpen && (
        <DocumentSubTypes
          {...subTypeDisclosure}
          currentRecord={currentRecord}
        />
      )}
      {dataTypeSourcesDisclosure.isOpen && (
        <SourcesByDataType
          {...dataTypeSourcesDisclosure}
          currentRecord={currentRecord}
        />
      )}
      {dataSourcesDisclosure.isOpen && (
        <DocumentTypeDataSources
          {...dataSourcesDisclosure}
          currentRecord={currentRecord}
          type="type"
        />
      )}
      {dataSourcesByResourceType.isOpen && (
        <DataSourcesByResourceType
          {...dataSourcesByResourceType}
          currentRecord={currentRecord}
        />
      )}
      {syncLabelsDisclosure.isOpen && (
        <SyncLabels
          isOpen={syncLabelsDisclosure.isOpen}
          onClose={syncLabelsDisclosure.onClose}
        />
      )}
    </Stack>
  );

  return isExpanded ? (
    <Drawer
      isOpen={isExpanded}
      onClose={() => setIsExpanded(false)}
      body={renderTable()}
      header={renderDataSecurityHeader(selectedTab)}
      closeButton
      styles={{
        content: { maxW: 'full' },
        //drawer: { portalProps: { containerRef: ref as any } },
      }}
    />
  ) : (
    renderTable()
  );
};
